import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { TranslocoService } from '@ngneat/transloco';
import { SharedService } from 'app/shared/shared.service';
import { take } from 'rxjs';

interface Language {
    id: string;
    label: string;
}

@Component({
    selector: 'languages',
    templateUrl: './languages.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'languages'
})
export class LanguagesComponent implements OnInit, OnDestroy {
    languages: Language[] = [
        { id: 'br', label: 'Português' },
        { id: 'es', label: 'Español' },
    ];
    activeLanguage: Language;

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseNavigationService: FuseNavigationService,
        private _translocoService: TranslocoService,
        private _sharedService: SharedService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        const savedLang = localStorage.getItem('selectedLanguage');
        this.activeLanguage = this.languages.find(l => l.id === savedLang)
            ?? this.languages.find(l => l.id === this._translocoService.getActiveLang())
            ?? this.languages[0];

        this._syncCookies(this.activeLanguage);

        this._translocoService.langChanges$.subscribe((activeLang) => {
            this._updateNavigation(activeLang);
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set the active lang
     *
     * @param lang
     */
    setActiveLanguage(langId: string): void {
        const lang = this.languages.find(l => l.id === langId);
        if (!lang) return;

        this.activeLanguage = lang;
        localStorage.setItem('selectedLanguage', lang.id);
        this._syncCookies(lang);
        this._translocoService.setActiveLang(lang.id);
        window.dispatchEvent(new CustomEvent('countryChanged', { detail: lang.id }));

        this._sharedService.simplePutEntity('user/me/locale', { locale: lang.id }).pipe(take(1)).subscribe();
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the navigation
     *
     * @param lang
     * @private
     */
    private _syncCookies(lang: Language): void {
        document.cookie = `selectedLanguage=${lang.id}; path=/; max-age=31536000; SameSite=Lax`;
    }

    private _updateNavigation(lang: string): void {
        // Get the component -> navigation data -> item
        const navComponent = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');

        // Return if the navigation component does not exist
        if (!navComponent) {
            return null;
        }

        // Get the flat navigation data
        const navigation = navComponent.navigation;

        // Update navigation items with translations
        this._updateNavigationItem('auranow', 'navigation.aura-now', navigation, navComponent);
        this._updateNavigationItem('tasks', 'navigation.tasks', navigation, navComponent);
        this._updateNavigationItem('dashboards', 'navigation.dashboards', navigation, navComponent);
        this._updateNavigationItem('apps', 'navigation.apps', navigation, navComponent);
        this._updateNavigationItem('dashboards.innovation-hub', 'navigation.innovation-hub', navigation, navComponent);
        this._updateNavigationItem('apps.innovation', 'navigation.innovation', navigation, navComponent);
        this._updateNavigationItem('apps.innovation.dashboard', 'navigation.innovation-dashboard', navigation, navComponent);
        this._updateNavigationItem('apps.innovation.new', 'navigation.innovation-new-project', navigation, navComponent);
        this._updateNavigationItem('apps.innovation.new-public', 'navigation.innovation-new-public', navigation, navComponent);
        this._updateNavigationItem('apps.innovation.search', 'navigation.innovation-search', navigation, navComponent);
        this._updateNavigationItem('apps.innovation.reports', 'navigation.innovation-reports', navigation, navComponent);
        this._updateNavigationItem('apps.innovation.settings', 'navigation.innovation-settings', navigation, navComponent);
        this._updateNavigationItem('apps.user-manual', 'navigation.user-manual', navigation, navComponent);
        this._updateNavigationItem('admin', 'navigation.admin', navigation, navComponent);
        this._updateNavigationItem('pages.user-management', 'navigation.user-management', navigation, navComponent);
        this._updateNavigationItem('pages.user-activity', 'navigation.user-activity', navigation, navComponent);
        this._updateNavigationItem('flow.participate', 'navigation.flow-participate', navigation, navComponent);
        this._updateNavigationItem('flow.follow', 'navigation.flow-follow', navigation, navComponent);
        this._updateNavigationItem('flow.committee', 'navigation.flow-committee', navigation, navComponent);
        this._updateNavigationItem('help', 'navigation.help', navigation, navComponent);
        this._updateClassificationItems(navigation, navComponent);
        this._updateNavigationItem('management-hub', 'navigation.management-hub', navigation, navComponent);

        // Update subtitles
        this._updateNavigationSubtitle('dashboards', 'navigation.dashboards-subtitle', navigation, navComponent);
        this._updateNavigationSubtitle('apps', 'navigation.apps-subtitle', navigation, navComponent);
        this._updateNavigationSubtitle('apps.innovation.dashboard', 'navigation.innovation-dashboard-subtitle', navigation, navComponent);
        this._updateNavigationSubtitle('apps.innovation.new', 'navigation.innovation-new-project-subtitle', navigation, navComponent);
        this._updateNavigationSubtitle('apps.innovation.new-public', 'navigation.innovation-new-public-subtitle', navigation, navComponent);
        this._updateNavigationSubtitle('tasks', 'navigation.tasks-subtitle', navigation, navComponent);
        this._updateNavigationSubtitle('apps.innovation.search', 'navigation.innovation-search-subtitle', navigation, navComponent);
        this._updateNavigationSubtitle('apps.user-manual', 'navigation.user-manual-subtitle', navigation, navComponent);
    }

    /**
     * Traduz os itens de "Avaliar Iniciativas", montados em runtime pelo
     * NavigationService — ele guarda o estado da janela em `item.meta`.
     */
    private _updateClassificationItems(navigation: any, navComponent: any): void {
        const group = this._fuseNavigationService.getItem('flow.committee', navigation);
        const apply = (items: any[]): void => {
            for (const item of items || []) {
                const meta = item.meta;
                if (meta?.classificationParent) {
                    this._translocoService.selectTranslate('navigation.classification').pipe(take(1))
                        .subscribe((t) => { item.title = t; navComponent.refresh(); });
                    this._translocoService.selectTranslate('navigation.classification-units-open', { open: meta.openCount, total: meta.total }).pipe(take(1))
                        .subscribe((t) => { item.subtitle = t; navComponent.refresh(); });
                    apply(item.children);
                } else if (meta?.classification) {
                    const badgeKey = meta.isOpen ? 'classification-badge-open' : meta.opensLabel ? 'classification-badge-opens' : 'classification-badge-closed';
                    this._translocoService.selectTranslate(`navigation.${badgeKey}`, { date: meta.opensLabel }).pipe(take(1))
                        .subscribe((t) => { item.badge = { ...item.badge, title: t }; navComponent.refresh(); });
                    if (!meta.isOpen) {
                        const tipKey = meta.opensLabel ? 'classification-tooltip-opens' : 'classification-tooltip-closed';
                        this._translocoService.selectTranslate(`navigation.${tipKey}`, { date: meta.opensLabel }).pipe(take(1))
                            .subscribe((t) => { item.tooltip = t; navComponent.refresh(); });
                    }
                    // Item único (uma unidade só) usa o título genérico e a unidade no subtítulo.
                    if (item.subtitle) {
                        this._translocoService.selectTranslate('navigation.classification').pipe(take(1))
                            .subscribe((t) => { item.title = t; navComponent.refresh(); });
                    }
                }
            }
        };
        apply(group?.children);
    }

    /**
     * Update a specific navigation item title
     *
     * @param itemId
     * @param translationKey
     * @param navigation
     * @param navComponent
     * @private
     */
    private _updateNavigationItem(itemId: string, translationKey: string, navigation: any, navComponent: any): void {
        const item = this._fuseNavigationService.getItem(itemId, navigation);
        if (item) {
            this._translocoService.selectTranslate(translationKey).pipe(take(1))
                .subscribe((translation) => {
                    item.title = translation;
                    navComponent.refresh();
                });
        }
    }

    /**
     * Update a specific navigation item subtitle
     *
     * @param itemId
     * @param translationKey
     * @param navigation
     * @param navComponent
     * @private
     */
    private _updateNavigationSubtitle(itemId: string, translationKey: string, navigation: any, navComponent: any): void {
        const item = this._fuseNavigationService.getItem(itemId, navigation);
        if (item) {
            this._translocoService.selectTranslate(translationKey).pipe(take(1))
                .subscribe((translation) => {
                    item.subtitle = translation;
                    navComponent.refresh();
                });
        }
    }
}
